import type { DebouncedFunc, ThrottleSettings } from 'lodash-es';
import throttle from 'lodash/throttle';
import { useEffect, useMemo, useRef } from 'react';
import type { Plugin } from '../types';

const useThrottlePlugin: Plugin<any, any[]> = (fetchInstance, { throttleWait, throttleLeading, throttleTrailing }) => {
  const throttledRef = useRef<DebouncedFunc<any>>();

  const options: ThrottleSettings = useMemo(() => {
    if (throttleLeading !== undefined) {
      return {
        leading: throttleLeading,
      };
    }
    if (throttleTrailing !== undefined) {
      return {
        trailing: throttleLeading,
      };
    }
  }, [throttleLeading, throttleTrailing]);

  useEffect(() => {
    if (throttleWait) {
      const _originRunAsync = fetchInstance.runAsync.bind(fetchInstance);

      throttledRef.current = throttle(
        (callback) => {
          callback();
        },
        throttleWait,
        options,
      );

      // throttle runAsync should be promise
      // https://github.com/lodash/lodash/issues/4400#issuecomment-834800398
      fetchInstance.runAsync = (...args) => {
        return new Promise((resolve, reject) => {
          throttledRef.current?.(() => {
            _originRunAsync(...args)
              .then(resolve)
              .catch(reject);
          });
        });
      };

      return () => {
        fetchInstance.runAsync = _originRunAsync;
        throttledRef.current?.cancel();
      };
    }
  }, [throttleWait, throttleLeading, throttleTrailing, fetchInstance, options]);

  if (!throttleWait) {
    return {};
  }

  return {
    onCancel: () => {
      throttledRef.current?.cancel();
    },
  };
};

export default useThrottlePlugin;
